import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { BrowserAudioFlow } from '../../../services/audio-flow';
import ChatClient, {
  CloseEvent,
  ErrorEvent,
  ResponseAudioEvent,
  ResponseInterruptedEvent,
} from '../clients/chat-client';
import {
  isTemporaryUnavailableError,
  TEMPORARY_UNAVAILABLE_MESSAGE,
} from '../temporary-unavailability';
import { useChatClientHealthCheck } from './use-chat-client-health-check';

const INITIAL_DELAY = 1000;

type UseChatClientProps = {
  audioFlow?: BrowserAudioFlow;
  apiUrl: string;
  interviewId: string;
  accessToken: string | null;
  requestedProvider?: 'aurora' | 'polaris' | 'zenith';
  onResponseAudio?: (event: ResponseAudioEvent) => void;
  onResponseInterrupted?: (event: ResponseInterruptedEvent) => void;
  onConnectionUnstable?: () => void;
  onConnectionLost?: () => void;
  onConnectionRecovered?: () => void;
  onError?: (event: ErrorEvent) => void;
  onClosed?: (event: CloseEvent) => void;

  /** Invoked when the interview has been started */
  onStarted?: () => void;

  /** Invoked when the server wants us to complete the interview */
  onComplete?: () => void;

  /** Invoked when the interview has been completed */
  onCompleted?: () => void;

  updateAssessmentCache?: (startedAt: Date) => void;
};

const useChatClient = ({
  audioFlow,
  apiUrl,
  interviewId,
  accessToken,
  requestedProvider,
  onResponseAudio,
  onResponseInterrupted,
  onConnectionUnstable,
  onConnectionLost,
  onConnectionRecovered,
  onError,
  onClosed,
  onStarted,
  onComplete,
  onCompleted,
  updateAssessmentCache,
}: UseChatClientProps) => {
  const [chatClient, setChatClient] = useState<ChatClient | null>(null);
  const [videoUploadUrl, setVideoUploadUrl] = useState<string | null>(null);
  const [screenRecordingUploadUrl, setScreenRecordingUploadUrl] = useState<
    string | null
  >(null);
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [inputSampleRate, setInputSampleRate] = useState<number | null>(null);

  useChatClientHealthCheck({
    chatClient,
    enabled: !!startedAt,
    onConnectionUnstable,
    onConnectionLost,
    onConnectionRecovered,
  });

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const chatClient = new ChatClient(apiUrl, audioFlow);

    const timeoutId = setTimeout(async () => {
      console.log('Connecting to chat server...');
      try {
        await chatClient.connect();
      } catch (err) {
        console.error('Failed to connect to chat server:', err);
        onError?.({
          type: 'error',
          id: uuidv4(),
          timestamp: Date.now(),
          error: {
            type: 'connection_error',
            message: TEMPORARY_UNAVAILABLE_MESSAGE,
            messageId: 'connection_failure',
          },
        });
        return;
      }

      const inviteCode = new URLSearchParams(window.location.search).get(
        'invite_code',
      );
      console.log('Starting interview...');
      chatClient.startInterview(
        interviewId,
        accessToken,
        inviteCode,
        requestedProvider,
      );

      chatClient.on('interview.started', (event) => {
        console.log('Interview started successfully.');
        setChatClient(chatClient);
        setVideoUploadUrl(event.videoUploadUrl);
        setScreenRecordingUploadUrl(event.screenRecordingUploadUrl ?? null);
        setStartedAt(event.startedAt);
        setDuration(event.duration);
        setInputSampleRate(event.inputSampleRate);
        updateAssessmentCache?.(event.startedAt);
        onStarted?.();
      });

      chatClient.on('error', (event) => {
        console.error('Chat client error:', event.error);
        if (isTemporaryUnavailableError(event.error)) {
          chatClient.disconnect('frontend_error', 'zara_busy');
          setChatClient(null);
        }
        onError?.(event);
      });

      chatClient.on('close', (event) => {
        const { code, reason, wasClean } = event;
        console.log('Chat client connection closed', {
          code,
          reason,
          wasClean,
        });
        onClosed?.(event);
      });
    }, INITIAL_DELAY);

    // Capture browser-driven closes (tab close, refresh, navigate-away).
    // Without this the WebSocket closes silently with code 1001 and the
    // reason is opaque server-side. `pagehide` is preferred over
    // `beforeunload` because it fires reliably on mobile, bfcache, and
    // doesn't run before a navigation the user might still cancel.
    const handlePageHide = () => {
      if (chatClient.isConnected()) {
        chatClient.disconnect('page_hide');
      }
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('pagehide', handlePageHide);
      if (chatClient.isConnected()) {
        console.log('Closing connection to chat server.');
        chatClient.disconnect('user_action_quit', 'effect_cleanup');
      }
      setChatClient(null);
    };
  }, [
    apiUrl,
    audioFlow,
    interviewId,
    accessToken,
    requestedProvider,
    onStarted,
    onError,
    onClosed,
    updateAssessmentCache,
  ]);

  useEffect(() => {
    if (!chatClient) {
      return;
    }

    if (onResponseAudio) {
      chatClient.on('response.audio', onResponseAudio);
    }

    if (onResponseInterrupted) {
      chatClient.on('response.interrupted', onResponseInterrupted);
    }

    if (onError) {
      chatClient.on('error', onError);
    }

    if (onComplete) {
      chatClient.on('interview.complete', onComplete);
    }

    if (onCompleted) {
      chatClient.on('interview.completed', onCompleted);
    }

    return () => {
      if (onResponseAudio) {
        chatClient.off('response.audio', onResponseAudio);
      }

      if (onResponseInterrupted) {
        chatClient.off('response.interrupted', onResponseInterrupted);
      }

      if (onError) {
        chatClient.off('error', onError);
      }

      if (onComplete) {
        chatClient.off('interview.complete', onComplete);
      }

      if (onCompleted) {
        chatClient.off('interview.completed', onCompleted);
      }
    };
  }, [
    chatClient,
    onResponseAudio,
    onResponseInterrupted,
    onError,
    onComplete,
    onCompleted,
  ]);

  const chatClientRef = useRef<ChatClient | null>(null);
  useEffect(() => {
    chatClientRef.current = chatClient;
  }, [chatClient]);

  const appendInputAudio = useCallback(
    (buffer: ArrayBuffer) => {
      const currentChatClient = chatClientRef.current;
      if (!currentChatClient) {
        if (audioFlow)
          audioFlow.counters.inputNoClientBytes += buffer.byteLength;
        return;
      }

      return currentChatClient.appendInputAudio(buffer);
    },
    [audioFlow],
  );

  const appendInputImage = useCallback((imageBase64: string) => {
    const currentChatClient = chatClientRef.current;
    if (!currentChatClient) {
      return;
    }

    return currentChatClient.appendInputImage(imageBase64);
  }, []);

  const completeInterview = useCallback(() => {
    const currentChatClient = chatClientRef.current;
    if (!currentChatClient) {
      return;
    }

    currentChatClient.completeInterview();
  }, []);

  const sendPlaybackComplete = useCallback(() => {
    const currentChatClient = chatClientRef.current;
    if (!currentChatClient) {
      return;
    }

    currentChatClient.sendPlaybackComplete();
  }, []);

  return {
    isStarted: chatClient !== null,
    videoUploadUrl,
    screenRecordingUploadUrl,
    startedAt,
    duration,
    inputSampleRate,
    appendInputAudio,
    appendInputImage,
    completeInterview,
    sendPlaybackComplete,
  };
};

export default useChatClient;
